import { VStack, Text, Spinner, Center, Grid } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { useHLOpenOrders } from '@/lib/hooks/useOpenOrders';
import { OpenOrderCard } from './OpenOrderCard';
import type { HexString } from '@/lib/config/wagmi';

interface OpenOrderSectionProps {
  userAddress: HexString;
  onViewAll?: () => void;
  hideHeader?: boolean;
}

export function OpenOrderSection({
  userAddress,
  onViewAll,
  hideHeader = false,
}: OpenOrderSectionProps) {
  const t = useTranslations('trader.orders');
  const { data: openOrders, isLoading, error } = useHLOpenOrders(userAddress);

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="blue.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={8}>
        <Text color="red.500" fontSize="sm">
          {t('error')}
        </Text>
      </Center>
    );
  }

  if (!openOrders || openOrders.length === 0) {
    return (
      <VStack gap={4} py={8}>
        <Text fontSize="sm" color="white/60" textAlign="center">
          {t('noOrders')}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack gap={4} align="stretch" w="full" px={4}>
      {!hideHeader && (
        <VStack gap={2} align="start">
          <Text fontSize="lg" fontWeight="semibold" color="white">
            {t('title')}
          </Text>
          <Text fontSize="sm" color="white/60">
            {t('count')}
          </Text>
        </VStack>
      )}

      <Grid gap={3} templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}>
        {openOrders.map((order, index) => (
          <OpenOrderCard key={`${order.oid}-${index}`} order={order} />
        ))}
      </Grid>

      {onViewAll && openOrders.length > 0 && (
        <Text
          fontSize="sm"
          color="blue.400"
          textAlign="center"
          cursor="pointer"
          _hover={{ color: 'blue.300' }}
          onClick={onViewAll}
        >
          {t('viewAll')}
        </Text>
      )}
    </VStack>
  );
}
